import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getCsrfToken } from 'utils/csrf';
import { trackEvent } from '../../../configureMixpanel';

const CrosstabsStep6 = ({ 
  // Step 1 data
  selectedDataset,
  surveyResponseTable,
  questionLabelsTable,
  responseLabelsTable,
  crosstabName,
  // Step 2 data
  questionIdField,
  optionTextField,
  responseField,
  parentQuestionIdField,
  weightKeyField,
  // Step 4 data
  formattedCrosstabsData,
  // Callbacks
  onComplete 
}) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const submissionRef = useRef(false);
  const pollingRef = useRef(null);
  const timeoutRef = useRef(null);
  const submissionStartTimeRef = useRef(null);

  // Function to clean up and redirect to list view
  const redirectToList = () => {
    // Clear all timers
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Clean up session storage
    sessionStorage.removeItem('crosstabFormData');
    
    // Call completion callback
    if (onComplete) {
      onComplete();
    }
    
    // Navigate to crosstabs list
    navigate(`/projects/${projectId}/crosstabs`);
  };

  const generateCompleteSubmissionPayload = () => {
    if (!formattedCrosstabsData) {
      throw new Error('No crosstab configuration available');
    }

    // Build complete original configuration for reuse functionality
    const originalConfig = {
      // Step 1 data
      selectedDataset,
      surveyResponseTable,
      questionLabelsTable,
      responseLabelsTable,
      crosstabName,
      // Step 2 data  
      questionIdField,
      optionTextField,
      responseField,
      parentQuestionIdField,
      weightKeyField,
      // Step 4 data (from formattedCrosstabsData)
      userConfigurations: formattedCrosstabsData
    };
    
    // Build full table names from Step 1 props (all are strings, not objects)
    const fullSurveyResponsesTable = `${projectId}.${selectedDataset}.${surveyResponseTable}`;
    const fullCnlTable = `${projectId}.${selectedDataset}.${questionLabelsTable}`;
    const fullVvlTable = `${projectId}.${selectedDataset}.${responseLabelsTable}`;
    
    // Generate output filename
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const outputFilename = `crosstab_${timestamp}.xlsx`;
    
    const detailedColumns = formattedCrosstabsData.columns;
    const detailedRows = formattedCrosstabsData.rows;

    const payload = {
      name: crosstabName || `Crosstab ${new Date().toLocaleDateString()}`,
      survey_responses_table: fullSurveyResponsesTable,
      cnl_table: fullCnlTable,
      vvl_table: fullVvlTable,
      columns: detailedColumns,
      rows: detailedRows,
      output_filename: outputFilename,
      weight_key: weightKeyField || null,
      value_collapses: formattedCrosstabsData.value_collapses || {},
      original_config: originalConfig
    };
    
    return payload;
  };

  // Submit crosstab to backend
  const submitCrosstab = async () => {
    console.log('Step 5 - Starting crosstab submission...');
    
    try {
      const payload = generateCompleteSubmissionPayload();
      
      const csrfToken = getCsrfToken();
      console.log('Step 5 - CSRF Token:', csrfToken);
      
      const response = await fetch(`/crosstabs/submit/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify(payload),
      });

      console.log('Step 5 - Response status:', response.status);
      console.log('Step 5 - Response headers:', Object.fromEntries(response.headers.entries()));

      const result = await response.json();
      console.log('Step 5 - API Response:', result);

      if (!response.ok) {
        console.error('Step 5 - Submission failed:', result);
        console.error('Step 5 - Request payload was:', payload);
        console.error('Step 5 - Request headers were:', {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || 'NO_TOKEN',
        });
        throw new Error(result.error || `Failed to submit crosstab (${response.status})`);
      }

      // Check if the crosstab submission was successful
      if (result.success && result.job_id) {
        console.log('Step 5 - Crosstab job created successfully:', result.job_id);
        return result;
      } else if (result.success === false) {
        throw new Error(result.error || 'Crosstab submission failed');
      } else {
        throw new Error('Unexpected response format from server');
      }
    } catch (error) {
      console.error('Step 5 - Error in submitCrosstab:', error);
      throw error;
    }
  };

  // Poll job status
  const pollJobStatus = async (jobId) => {
    try {
      const response = await fetch(`/crosstabs/jobs/${jobId}/status/`, {
        credentials: 'include',
        headers: {
          'X-CSRFToken': getCsrfToken() || '',
        }
      });
      
      if (response.status === 403) {
        console.error('Step 5 - Authentication failed during polling, redirecting to list');
        setStatusMessage('Authentication issue. Redirecting to crosstabs list...');
        setTimeout(() => redirectToList(), 1000);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to get job status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Step 5 - Job status update:', data);
      
      setJobStatus(data.job.state);
      setStatusMessage(data.message || '');
      
      // If job is complete, stop polling and handle result
      if (data.job.state === 'SUCCEEDED') {
        // Clear the polling interval immediately - otherwise the next tick
        // (state can't revert from SUCCEEDED) re-enters this branch and
        // double-fires crosstab_job_completed before the redirect below runs.
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        trackEvent('crosstab_job_completed', {
          success: true,
          elapsed_seconds: submissionStartTimeRef.current
            ? (Date.now() - submissionStartTimeRef.current) / 1000
            : undefined,
        });
        // Redirect to crosstabs list where download link will be available
        setTimeout(() => {
          redirectToList();
        }, 2000);

      } else if (data.job.state === 'FAILED') {
        trackEvent('crosstab_job_completed', {
          success: false,
          elapsed_seconds: submissionStartTimeRef.current
            ? (Date.now() - submissionStartTimeRef.current) / 1000
            : undefined,
        });
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        setError(data.message || 'Crosstab generation failed');
        setIsSubmitting(false);
      }
      // If still PENDING or RUNNING, continue polling
      
    } catch (error) {
      console.error('Step 5 - Error polling job status:', error);
      
      // On authentication errors, redirect to list view
      if (error.message.includes('403') || error.message.includes('Authentication')) {
        console.log('Step 5 - Authentication error during polling, redirecting to list');
        setStatusMessage('Authentication issue. Redirecting to crosstabs list...');
        setTimeout(() => redirectToList(), 1000);
        return;
      }
      
      // On other errors, stop polling and show error
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setError('Failed to check job status');
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const processSubmission = async () => {
      // Prevent multiple submissions (React StrictMode can cause double-runs)
      if (isSubmitting || submissionRef.current) {
        return;
      }
      setIsSubmitting(true);
      submissionRef.current = true;
      submissionStartTimeRef.current = Date.now();

      try {
        // Submit the crosstab
        const result = await submitCrosstab();
        trackEvent('crosstab_submitted', { source: 'wizard', success: true });

        // Start polling for job status
        setJobId(result.job_id);
        setJobStatus(result.state);
        setStatusMessage(result.message);
        
        // Set 5-second timeout to redirect to list view if no completion
        timeoutRef.current = setTimeout(() => {
          console.log('Step 5 - 5-second timeout reached, redirecting to list view');
          setStatusMessage('Your crosstab is processing. You can check its status in the crosstabs list.');
          redirectToList();
        }, 5000);
        
        // Start polling every 2 seconds (more frequent since we have a short timeout)
        pollingRef.current = setInterval(() => {
          pollJobStatus(result.job_id);
        }, 2000);
        
        // Also poll immediately
        pollJobStatus(result.job_id);
        
      } catch (error) {
        console.error('Step 5 - Submission error:', error);
        trackEvent('crosstab_submitted', { source: 'wizard', success: false });
        setError(error.message);
        setIsSubmitting(false);
        submissionRef.current = false; // Allow retry on submission error
      }
    };

    processSubmission();
  }, []); // Empty dependency array - run only once

  // Cleanup polling and timeout on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center'
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: '600px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to create crosstab
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please check the browser console for more details and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px',
        textAlign: 'center'
      }}
    >
      <CircularProgress size={60} sx={{ mb: 3 }} />
      <Typography variant="h5" sx={{ mb: 2 }}>
        {jobStatus === 'PENDING' ? 'Queuing crosstab...' : 
         jobStatus === 'RUNNING' ? 'Generating crosstab...' : 
         'Processing crosstab...'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        {statusMessage || 'Processing your crosstab and generating the download file. This may take several minutes.'}
      </Typography>
      {jobId && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: 'monospace' }}>
          Job ID: {jobId}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        You'll be redirected to the crosstabs list when ready.
      </Typography>
    </Box>
  );
};

export default CrosstabsStep6;